import { useCallback, useEffect, useMemo, useState } from 'react';
import MetaTags from 'react-meta-tags';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Alert, Button, Card, CardBody, Col, Input, Label, Row, Spinner, Table } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { getTownhouseOptions, getFlushRecords, deleteFlushRecord } from './Api';
import { HotWaterTankFormModal, formatDate } from './HotWaterTankFormModal';

const STATUS_BADGES = {
  overdue: { text: 'Overdue', color: 'danger' },
  due_soon: { text: 'Due soon', color: 'warning' },
  ok: { text: 'Up to date', color: 'success' },
  superseded: { text: 'Superseded', color: 'secondary' },
};

const currentUser = () => {
  try {
    const token = localStorage.getItem('accessToken');
    return token ? jwt_decode(token) : null;
  } catch {
    return null;
  }
};

const dueText = (row) => {
  if (row.due_status === 'superseded' || row.days_until_due == null) return '';
  if (row.days_until_due < 0) return `${Math.abs(row.days_until_due)} days overdue`;
  if (row.days_until_due === 0) return 'Due today';
  return `in ${row.days_until_due} days`;
};

const HotWaterTankTracker = () => {
  const user = useMemo(currentUser, []);
  const hasAccess = user?.role === 'company' || (user?.role === 'technical staff' && !!user?.lead);

  const [options, setOptions] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ propertyId: '', buildingName: '', status: 'all', latestOnly: true, search: '' });
  const [modal, setModal] = useState({ open: false, record: null });

  useEffect(() => {
    if (!hasAccess) return;
    getTownhouseOptions().then(setOptions).catch(err => toast.error(err.message));
  }, [hasAccess]);

  const loadRecords = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    try {
      const params = { latestOnly: filters.latestOnly ? 'true' : 'false' };
      if (filters.propertyId) params.propertyId = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      setRecords(await getFlushRecords(params));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, filters.propertyId, filters.buildingName, filters.latestOnly]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const counts = useMemo(() => records.reduce((acc, r) => {
    acc[r.due_status] = (acc[r.due_status] || 0) + 1;
    return acc;
  }, {}), [records]);

  const visible = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    return records
      .filter(r => filters.status === 'all' || r.due_status === filters.status)
      .filter(r => !term || String(r.unit_no).toLowerCase().includes(term))
      .sort((a, b) => {
        if (a.is_latest !== b.is_latest) return a.is_latest ? -1 : 1;
        return new Date(a.next_due_date) - new Date(b.next_due_date);
      });
  }, [records, filters.status, filters.search]);

  const selectedProperty = options.find(p => p.property_id === filters.propertyId);

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      title: 'Delete this record?',
      text: `Unit ${row.unit_no}, flushed ${formatDate(row.maintenance_date)}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteFlushRecord(row._id);
      toast.success('Record deleted');
      loadRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const canDelete = (row) => user?.role === 'company' || String(row.recorded_by?.object_id) === String(user?.id);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Hot Water Tank Tracker</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Tracker" breadcrumbItem="Hot Water Tank Tracker" />

        {!hasAccess ? (
          <Alert color="warning">
            The hot water tank tracker is available to company users and technical staff leads only.
          </Alert>
        ) : (
          <>
            <Card>
              <CardBody>
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Label>Property</Label>
                    <Input
                      type="select"
                      value={filters.propertyId}
                      onChange={e => setFilters(f => ({ ...f, propertyId: e.target.value, buildingName: '' }))}
                    >
                      <option value="">All townhouse properties</option>
                      {options.map(p => <option key={p.property_id} value={p.property_id}>{p.title}</option>)}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label>Building</Label>
                    <Input
                      type="select"
                      value={filters.buildingName}
                      disabled={!selectedProperty}
                      onChange={e => setFilters(f => ({ ...f, buildingName: e.target.value }))}
                    >
                      <option value="">All buildings</option>
                      {(selectedProperty?.buildings || []).map(b => <option key={b} value={b}>{b}</option>)}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label>Status</Label>
                    <Input
                      type="select"
                      value={filters.status}
                      onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    >
                      <option value="all">All</option>
                      <option value="overdue">Overdue</option>
                      <option value="due_soon">Due soon (60 days)</option>
                      <option value="ok">Up to date</option>
                      {!filters.latestOnly && <option value="superseded">Superseded</option>}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Label>Unit</Label>
                    <Input
                      type="search"
                      placeholder="Search unit"
                      value={filters.search}
                      onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                    />
                  </Col>
                  <Col md={3} className="d-flex flex-column gap-2">
                    <div className="form-check form-switch">
                      <input
                        id="hwt-latest-only"
                        className="form-check-input"
                        type="checkbox"
                        checked={filters.latestOnly}
                        onChange={e => setFilters(f => ({
                          ...f,
                          latestOnly: e.target.checked,
                          status: e.target.checked && f.status === 'superseded' ? 'all' : f.status,
                        }))}
                      />
                      <label className="form-check-label" htmlFor="hwt-latest-only">Latest flush per unit only</label>
                    </div>
                    <Button
                      color="primary"
                      onClick={() => setModal({ open: true, record: null })}
                      disabled={!options.length}
                    >
                      <FeatherIcon icon="plus" size={14} className="me-1" />
                      Record Flush
                    </Button>
                  </Col>
                </Row>

                <div className="d-flex flex-wrap gap-2 mt-3">
                  {['overdue', 'due_soon', 'ok'].map(key => (
                    <span key={key} className={`badge bg-${STATUS_BADGES[key].color} fs-6 fw-normal`}>
                      {STATUS_BADGES[key].text}: {counts[key] || 0}
                    </span>
                  ))}
                </div>
              </CardBody>
            </Card>

            {!options.length && (
              <Alert color="info">No townhouse buildings found for your properties. The tracker is only for townhouses.</Alert>
            )}

            <Card>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Property / Building</th>
                        <th>Unit No.</th>
                        <th>Tank Drained</th>
                        <th>Maintenance Date</th>
                        <th>Next Due Date</th>
                        <th>Status</th>
                        <th>Attachments</th>
                        <th>Recorded By</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={9} className="text-center py-5"><Spinner /></td></tr>
                      ) : !visible.length ? (
                        <tr><td colSpan={9} className="text-center text-muted py-5">No hot water tank records to display</td></tr>
                      ) : visible.map(row => {
                        const badge = STATUS_BADGES[row.due_status] || STATUS_BADGES.ok;
                        return (
                          <tr key={row._id}>
                            <td>
                              <div className="fw-medium">{row.property_title || '—'}</div>
                              <small className="text-muted">{row.property_details?.building_name}</small>
                            </td>
                            <td>{row.unit_no}</td>
                            <td>
                              <span className={`badge bg-${row.drained ? 'success' : 'danger'}`}>
                                {row.drained ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>{formatDate(row.maintenance_date)}</td>
                            <td>
                              <div>{formatDate(row.next_due_date)}</div>
                              <small className="text-muted">{dueText(row)}</small>
                            </td>
                            <td><span className={`badge bg-${badge.color}`}>{badge.text}</span></td>
                            <td>
                              {row.attachments?.length ? (
                                <button
                                  type="button"
                                  className="btn btn-link p-0"
                                  onClick={() => setModal({ open: true, record: row })}
                                >
                                  <FeatherIcon icon="paperclip" size={14} className="me-1" />
                                  {row.attachments.length}
                                </button>
                              ) : <span className="text-muted">None</span>}
                            </td>
                            <td>{row.recorded_by_name || row.recorded_by?.role || '—'}</td>
                            <td className="text-center text-nowrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-primary me-1"
                                title="Edit"
                                onClick={() => setModal({ open: true, record: row })}
                              >
                                <FeatherIcon icon="edit" size={14} />
                              </button>
                              {canDelete(row) && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-danger"
                                  title="Delete"
                                  onClick={() => handleDelete(row)}
                                >
                                  <FeatherIcon icon="trash-2" size={14} />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </>
        )}
      </div>

      <HotWaterTankFormModal
        isOpen={modal.open}
        record={modal.record}
        options={options}
        defaults={{ propertyId: filters.propertyId, buildingName: filters.buildingName }}
        userRole={user?.role}
        onClose={() => setModal({ open: false, record: null })}
        onSaved={() => {
          setModal({ open: false, record: null });
          loadRecords();
        }}
      />
    </div>
  );
};

export { HotWaterTankTracker };
